import { captureException } from "@sentry/react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { AppAuthResponse } from "../../hooks/api/useAppAuth";
import {
  fetchInsurelyFees,
  useGetInsurelyFees,
} from "../../hooks/api/useGetInsurelyFees";
import { usePostInsurelyInput } from "../../hooks/api/usePostInsurelyInput";
import { usePatchLead } from "../../hooks/api/usePatchLead";
import { useDeleteInsurelyData } from "../../hooks/api/useDeleteInsurelyData";
import { useUpdateCustomerData } from "../../hooks/ui/customerDataStore";
import { normalizeToE164 } from "../../utils/contactDetailsValidation";
import {
  collectionOutcomes,
  DATA_STATUSES,
} from "../../utils/insurelyCollections";
import { IntroScreen, type IntroStep } from "./IntroScreen";
import { InsurelyScreen } from "./InsurelyScreen";
import { CalculationScreen } from "./CalculationScreen";
import { ContactDrawer } from "./ContactDrawer";
import { ErrorScreen } from "./ErrorScreen";
import reloadIconUrl from "../../assets/reload-icon.svg?url";
import listIconUrl from "../../assets/list-icon.svg?url";
import walletIconUrl from "../../assets/wallet-icon.svg?url";

type AppStep = "intro" | "insurely" | "result";

export type PrefillData = {
  nationalId?: string;
  email?: string;
  phoneNumber?: string;
};

const COMPANIES = (import.meta.env.VITE_INSURELY_COMPANIES ?? "")
  .split(",")
  .map((company: string) => company.trim())
  .filter(Boolean);

const INTRO_STEPS: IntroStep[] = [
  {
    icon: reloadIconUrl,
    title: "Vi kollar din pension",
    description:
      "Vi ansluter till dina försäkringsbolag och kontrollerar automatiskt ditt nuvarande pensionssparande",
  },
  {
    icon: listIconUrl,
    title: "Du får en pensionsöversikt",
    description:
      "Du får en överblick över dina tjänste- och privatpensioner för att enklare få koll på ditt pensionssparande.",
  },
  {
    icon: walletIconUrl,
    title: "Prognos om förbättrad pension",
    description:
      "Om vi kan förbättra din pension ger vi dig en uppskattning om hur mycket vi kan öka ditt pensionssparande fram till pension.",
  },
];

export interface InsurelyFlowProps {
  auth?: AppAuthResponse;
  prefillData: PrefillData | null;
  isAuthLoading: boolean;
  isChatAvailable: boolean;
  onContactError: () => void;
}

export function InsurelyFlow({
  auth: data,
  prefillData,
  isAuthLoading: isLoading,
  isChatAvailable,
  onContactError,
}: InsurelyFlowProps) {
  const { customerData } = useUpdateCustomerData();
  const [step, setStep] = useState<AppStep>("intro");
  const [pendingCollections, setPendingCollections] = useState<Set<string>>(
    new Set(),
  );
  const [collectionGroupEnded, setCollectionGroupEnded] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  // Set when the user removes their pension data, cleared when they collect again.
  const [deleted, setDeleted] = useState(false);
  // Bumped when a collection round ends, so the fees endpoint is called again for the
  // pensions just added instead of serving the previous round from the query cache.
  const [calculationRound, setCalculationRound] = useState(0);
  // Companies Insurely pre-selects: the default list first, none for "Leta efter fler",
  // the ones already collected when the overview is refreshed.
  const [preselectedCompanies, setPreselectedCompanies] =
    useState<string[]>(COMPANIES);
  // company -> last COLLECTION_STATUS seen for it in this session.
  const [collectionStatuses, setCollectionStatuses] = useState<
    Record<string, string>
  >({});

  // collectionId -> company for every collection sent to the backend.
  const sentCollections = useRef(new Map<string, string>());

  const cachedCalculation = data?.appData?.cachedCalculation;
  const ctaClickedAt = data?.appData?.engagement?.ctaClickedAt;

  // Leads cached before policies were stored carry a teaser but nothing to fill the overview
  // with. Ignore that shape and recalculate instead of rendering an empty overview.
  const usableCache = Array.isArray(cachedCalculation?.policies)
    ? cachedCalculation
    : undefined;

  // A calculation that found nothing has no overview to resume to, so that lead starts at the
  // intro and collects instead of landing on an empty result.
  const emptyCache = usableCache?.policies?.length === 0;

  // A lead that already has a calculation (or has asked to be contacted) lands straight on the
  // result, no need to walk through Insurely again. Only once: a later refetch of the auth
  // data must not pull the user out of a collection round they just started.
  const resumedToResult = useRef(false);
  useEffect(() => {
    if (ctaClickedAt) setContactSent(true);
    if (
      !resumedToResult.current &&
      (ctaClickedAt || (cachedCalculation && !emptyCache))
    ) {
      resumedToResult.current = true;
      setStep("result");
    }
  }, [ctaClickedAt, cachedCalculation, emptyCache]);

  const { mutateAsync: fetchCollection } = usePostInsurelyInput(
    customerData.credentials,
  );
  const { mutateAsync: patchLead } = usePatchLead(customerData.credentials);
  const { mutateAsync: deleteInsurelyData } = useDeleteInsurelyData(
    customerData.credentials,
  );

  const {
    data: feesData,
    isLoading: feesLoading,
    isError: feesError,
    isFetching: feesFetching,
    refetch: refetchFees,
  } = useGetInsurelyFees({
    credentials: customerData.credentials,
    leadId: data?.leadId,
    round: calculationRound,
    // The cached calculation renders as-is, but only until this session collects something
    // new: from the first round onwards the fresh calculation wins.
    // `collectionGroupEnded` is still true while the collections of the round are being
    // posted - the result screen is already up, but the calculation waits for them.
    enabled:
      step === "result" &&
      !collectionGroupEnded &&
      (calculationRound > 0 || !usableCache),
  });

  // After a delete neither the fresh calculation nor the lead's cache describes what is left.
  const teaser = deleted ? undefined : (feesData?.teaser ?? usableCache);
  const policies = deleted
    ? []
    : (feesData?.policies ?? usableCache?.policies ?? []);

  // Companies that returned nothing. Known only for collections run in this session, so a
  // returning lead sees no such section rather than a stale one.
  const outcomes = useMemo(
    () => collectionOutcomes(collectionStatuses, policies),
    [collectionStatuses, policies],
  );

  // Stamp insurelyInitiatedAt once, deferred to an effect because leadId may
  // not have arrived when "Kom igång" is clicked. Skip existing customers and
  // leads that already had an engagement insurelyInitiatedAt.
  const insurelyInitiatedSent = useRef(false);
  useEffect(() => {
    const leadId = data?.leadId;
    if (
      step !== "intro" &&
      leadId &&
      !data?.isCustomer &&
      !data?.appData?.engagement?.insurelyInitiatedAt &&
      !insurelyInitiatedSent.current
    ) {
      insurelyInitiatedSent.current = true;
      patchLead({
        leadId,
        payload: {
          appData: {
            engagement: { insurelyInitiatedAt: new Date().toISOString() },
          },
        },
      }).catch(() => {});
    }
  }, [step, data, patchLead]);

  useEffect(() => {
    if (!prefillData?.nationalId) return;
    window.insurely = {
      config: {
        customerId: import.meta.env.VITE_INSURELY_CUSTOMER_ID,
        configName: import.meta.env.VITE_INSURELY_CONFIG_NAME,
      },
      prefill: {
        user: { swedishPersonalNumber: prefillData?.nationalId },
        ...(preselectedCompanies.length > 0 && {
          dataAggregation: { companies: preselectedCompanies },
        }),
      },
    };
    // Drop the prefilled SSN when the webview unmounts so it does not linger in window for the rest of the session
    return () => {
      delete window.insurely;
    };
  }, [prefillData, preselectedCompanies]);

  const nationalId = prefillData?.nationalId;

  useEffect(() => {
    const handleInsurelyMessage = (e: MessageEvent) => {
      const d = e.data;
      if (d?.origin !== "insurely") return;

      if (d.name === "COLLECTION_STATUS" && typeof d.value === "string") {
        const collectionId: string | undefined =
          d.extraInformation?.collectionId;
        const company: string | undefined = d.extraInformation?.company;
        const status: string = d.value;

        if (company) {
          setCollectionStatuses((statuses) => ({
            ...statuses,
            [company]: status,
          }));
        }

        if (
          DATA_STATUSES.includes(status) &&
          collectionId &&
          company &&
          !sentCollections.current.has(collectionId)
        ) {
          sentCollections.current.set(collectionId, company);
          setPendingCollections((pending) =>
            new Set(pending).add(collectionId),
          );
          fetchCollection({
            fid: customerData.credentials.identityId,
            collectionId,
            company,
            ...(nationalId && { nationalId }),
          })
            .catch((error) =>
              captureException(error, {
                tags: {
                  operation: "postInsurelyInput",
                  errorType: (error as Error)?.name || "UnknownError",
                },
                extra: {
                  apiEndpoint: "/mis/insurely-input",
                  company,
                  fid: customerData.credentials.identityId,
                },
                level: "error",
              }),
            )
            .finally(() =>
              setPendingCollections((pending) => {
                const next = new Set(pending);
                next.delete(collectionId);
                return next;
              }),
            );
        }
      }

      if (
        d.name === "COLLECTION_GROUP_ENDED" ||
        d.name === "RESULTS" ||
        (d.name === "PAGE_VIEW" && d.value === "DATA_AGGREGATION_RESULTS")
      ) {
        setCollectionGroupEnded(true);
      }
    };

    window.addEventListener("message", handleInsurelyMessage);
    return () => window.removeEventListener("message", handleInsurelyMessage);
  }, [fetchCollection, customerData.credentials.identityId, nationalId]);

  // Insurely is done collecting: leave its iframe for the calculation loader right away, and
  // only ask for the calculation once every collection of the round has reached the backend.
  // Clearing the flag in the same batch as the round bump hands the loader straight over to
  // the query, with no frame of stale result in between.
  useEffect(() => {
    if (!collectionGroupEnded) return;
    setStep("result");
    if (pendingCollections.size === 0) {
      setCollectionGroupEnded(false);
      setCalculationRound((round) => round + 1);
    }
  }, [pendingCollections, collectionGroupEnded]);

  // Returns whether the data is provably gone. Never throws: the caller shows the failure,
  // and an error that got lost on the way would read as a successful delete.
  const handleDelete = async () => {
    try {
      await deleteInsurelyData({ leadId: data?.leadId });

      // Confirm the removal rather than trusting it. No leadId: that would re-cache.
      const { policies: remaining } = await fetchInsurelyFees(
        customerData.credentials,
      );
      // Only an empty list is proof. A missing or malformed one is not.
      if (!Array.isArray(remaining) || remaining.length > 0) return false;
    } catch {
      return false;
    }

    sentCollections.current.clear();
    setPendingCollections(new Set());
    setCollectionStatuses({});
    setDeleted(true);
    return true;
  };

  if (step === "intro") {
    return (
      <IntroScreen
        title="Se din pensionsöversikt"
        steps={INTRO_STEPS}
        ctaLabel="Kontrollera min pension"
        onContinue={() => setStep("insurely")}
      />
    );
  }

  if (step === "insurely") {
    return (
      <InsurelyScreen isDataLoading={isLoading || !prefillData?.nationalId} />
    );
  }

  // Retry just the fees call, so the collections already made are kept.
  if (feesError) {
    return (
      <ErrorScreen isRetrying={feesFetching} onRetry={() => refetchFees()} />
    );
  }

  return (
    <>
      <CalculationScreen
        teaser={teaser}
        policies={policies}
        outcomes={outcomes}
        calculatedAt={feesData?.calculatedAt ?? usableCache?.calculatedAt}
        isLoading={feesLoading || collectionGroupEnded}
        contactSent={contactSent}
        showAskSam={isChatAvailable}
        onContact={() => setContactOpen(true)}
        onAddMore={() => {
          setDeleted(false);
          setPreselectedCompanies([]);
          setStep("insurely");
        }}
        onRefresh={() => {
          setPreselectedCompanies([
            ...new Set(policies.map((policy) => policy.company)),
          ]);
          setStep("insurely");
        }}
        onDelete={handleDelete}
        deleted={deleted}
      />
      <ContactDrawer
        opened={contactOpen}
        onClose={() => setContactOpen(false)}
        initialEmail={prefillData?.email ?? ""}
        initialPhone={prefillData?.phoneNumber ?? ""}
        onSubmit={async (email, phone, topics) => {
          const leadId = data?.leadId;
          try {
            // No lead to attach the request to means nothing would be stored, so fail the
            // submit instead of confirming a call the advisors never hear about.
            if (!leadId) throw new Error("Contact request without leadId");
            await patchLead({
              leadId,
              payload: {
                email,
                phone: normalizeToE164(phone),
                appData: {
                  engagement: { ctaClickedAt: new Date().toISOString() },
                  ...(topics.length > 0 && { contactTopics: topics }),
                },
              },
            });
            setContactSent(true);
          } catch {
            // Retrying from the error screen re-authenticates and restarts the flow.
            onContactError();
          }
          setContactOpen(false);
        }}
      />
    </>
  );
}
